import type { FC } from 'react';

import { Notification } from '@eneco/components-next/Notification/Notification';
import { StackContainer } from '@eneco/custom-components/selfservice';
import { SelfservicePageTitle } from '@eneco/custom-components/SelfservicePageTitle';
import { usePageTitle } from '@eneco/hooks/pageTitle';
import { Box, PageGrid, Stack, SkipToMain } from '@eneco/sparky';
import { Placeholder, useLayoutData, usePlaceholder } from '@sitecore/common';

export const SelfServiceTwoColumnLayout: FC = () => {
  const { route } = useLayoutData();

  const { title, subtitle } = usePageTitle();

  const hasInitialRightColumn = !!route.placeholders['jss-right']?.length;
  const hasInitialMainBottom = !!route.placeholders['jss-main-bottom']?.length;
  const { hasScopedComponents } = usePlaceholder();

  const hasRightColumn = hasScopedComponents('jss-right') || hasInitialRightColumn;
  const hasMainBottom = hasScopedComponents('jss-main-bottom') || hasInitialMainBottom;

  return (
    <>
      <Placeholder name="jss-meta" />

      <StackContainer>
        <SkipToMain.SkipLink />
        <header>
          <Placeholder name="jss-header" />
          <Placeholder name="jss-navigationbar" />
          <Placeholder name="jss-notifications" />
        </header>

        <Stack.Item grow>
          <PageGrid
            gridTemplateAreas={{
              // prettier-ignore
              initial: `
                                    "title  title  title  title  title  title  title  title  title "
                                    "main   main   main   main   main   main   main   main   main  "
                ${hasRightColumn ? '"right  right  right  right  right  right  right  right  right"' : ''}
                ${hasMainBottom ?  '"bottom bottom bottom bottom bottom bottom bottom bottom bottom"' : ''}
              `,
              // prettier-ignore
              md: `
                                    "title  title  title  title  title  title  title  title  title  title  title  title "
                                    "main   main   main   main   main   main   main   main   main   main   main   main  "
                ${hasRightColumn ? '"right  right  right  right  right  right  right  right  right  right  right  right "' : ''}
                ${hasMainBottom ?  '"bottom bottom bottom bottom bottom bottom bottom bottom bottom bottom bottom bottom"' : ''}
              `,
              // prettier-ignore
              lg: `
                                    "title  title  title  title  title  title  title  title  title  .     .     ."
                                    "main   main   main   main   main   main   main   main   main   right right right"
                ${hasMainBottom ?  '"bottom bottom bottom bottom bottom bottom bottom bottom bottom right right right"' : ''}
              `,
            }}
            gridTemplateRows="auto auto 1fr">
            {/* Title cell */}
            {title && title?.length > 0 && (
              <PageGrid.Item gridArea="title">
                <SelfservicePageTitle title={title} subtitle={subtitle} />
              </PageGrid.Item>
            )}

            {/* Combined left+main column */}
            <PageGrid.Item gridArea="main">
              <Stack gap={{ initial: '2', md: '3', lg: '4', xl: '6' }}>
                {/* Main content with notification */}
                <Notification />
                <SkipToMain.Main>
                  <Stack gap={{ initial: '2', md: '3', lg: '4', xl: '6' }}>
                    <Placeholder name="jss-main" />
                  </Stack>
                </SkipToMain.Main>
              </Stack>
            </PageGrid.Item>

            {/* Main bottom column */}
            {hasMainBottom && (
              <PageGrid.Item gridArea="bottom">
                <Stack gap={{ initial: '2', md: '3', lg: '4', xl: '6' }}>
                  <Placeholder name="jss-main-bottom" />
                </Stack>
              </PageGrid.Item>
            )}

            {/* Right column */}
            {hasRightColumn && (
              <PageGrid.Item gridArea="right">
                <Stack gap={{ initial: '2', md: '3', lg: '4', xl: '6' }}>
                  <Placeholder name="jss-right" />
                </Stack>
              </PageGrid.Item>
            )}
          </PageGrid>
        </Stack.Item>

        <footer>
          <Box paddingTop="3">
            <Placeholder name="jss-footer" />
            {/* Below placeholder is added to accomodate the Usabilla Feedback component on every page. */}
            <Placeholder name="jss-footer-bottom" />
          </Box>
        </footer>
      </StackContainer>
    </>
  );
};
